import styles from './ChipToggle.module.css';

export interface ChipOption<T extends string> {
  value: T;
  label: string;
}

interface ChipToggleProps<T extends string> {
  label: string;
  options: ChipOption<T>[];
  selected: readonly T[];
  onChange: (next: T[]) => void;
  disabled?: boolean;
  /** Single-choice (radio) instead of multi-select (checkbox). */
  single?: boolean;
}

/** Pill toggles as in Admin v2 (consents, assistance needs, MyKad | Passport). */
export function ChipToggle<T extends string>({
  label,
  options,
  selected,
  onChange,
  disabled,
  single,
}: ChipToggleProps<T>) {
  const toggle = (value: T) => {
    if (single) return onChange([value]);
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );
  };
  return (
    <div
      className={styles.group}
      role={single ? 'radiogroup' : 'group'}
      aria-label={label}
    >
      {options.map((o) => {
        const on = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            role={single ? 'radio' : 'checkbox'}
            aria-checked={on}
            disabled={disabled}
            className={[styles.chip, on && styles.on].filter(Boolean).join(' ')}
            onClick={() => toggle(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
